/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ibgeService, IBGECity, IBGEDistrict, IBGEState } from '../services/ibge-service';

@customElement('leads-list')
export class LeadsList extends LitElement {
  @property({ type: Array }) leads: any[] = [];
  @property({ type: Boolean }) isScanning = false;
  @property({ type: Number }) scanProgress = 0;
  @property({ type: String }) scanStatus = '';
  @property({ type: Boolean }) isEnriching = false;
  @property({ type: Number }) enrichedCount = 0;
  @property({ type: Boolean }) isWhatsAppEnriching = false;
  @property({ type: Number }) whatsappEnrichedCount = 0;
  @property({ type: Number }) totalCount = 0;

  @state() private formQuery = '';
  @state() private formCity = '';
  @state() private formMode: 'standard' | 'exhaustive' | 'micro' = 'standard';

  @state() private states: IBGEState[] = [];
  @state() private cities: IBGECity[] = [];
  @state() private neighborhoods: IBGEDistrict[] = [];
  
  @state() private selectedStateSigla = 'MG';
  @state() private selectedCityId: number = 3106200; // BH Default
  @state() private selectedNeighborhood: IBGEDistrict | null = null;
  @state() private customNeighborhood = '';
  @state() private isCumulative = false;

  async connectedCallback() {
    super.connectedCallback();
    try {
      this.states = await ibgeService.getStates();
      // Load initial cities for MG
      this.cities = await ibgeService.getCities('MG');
      // Load initial neighborhoods for BH
      this.neighborhoods = await ibgeService.getNeighborhoods(3106200);
      this.formCity = 'Belo Horizonte, MG';
    } catch (e) {
      console.error('Failed to load IBGE data', e);
    }
  }

  private async onStateChange(e: any) {
    this.selectedStateSigla = e.target.value;
    this.cities = await ibgeService.getCities(this.selectedStateSigla);
    this.neighborhoods = [];
    this.selectedNeighborhood = null;
    this.formCity = this.selectedStateSigla;
  }

  private async onCityChange(e: any) {
    const cityId = parseInt(e.target.value);
    const city = this.cities.find(c => c.id === cityId);
    if (!city) return;

    this.selectedCityId = cityId;
    this.formCity = `${city.nome}, ${this.selectedStateSigla}`;
    
    // Clear current neighborhoods and reset selection while loading
    this.neighborhoods = [];
    this.selectedNeighborhood = null;
    
    try {
      this.neighborhoods = await ibgeService.getNeighborhoods(cityId);
    } catch (e) {
      console.error('Error loading neighborhoods', e);
    }
  }

  private onNeighborhoodChange(e: any) {
    const nhId = parseInt(e.target.value);
    this.selectedNeighborhood = this.neighborhoods.find(n => n.id === nhId) || null;
  }

  private startManualScan() {
    if (!this.formQuery) return;
    
    let locationLabel = this.formCity;
    let locationObj: any = undefined;

    const neighborhoodName = this.customNeighborhood.trim() || (this.selectedNeighborhood ? this.selectedNeighborhood.nome : '');

    if (neighborhoodName) {
      const nhPrefix = neighborhoodName.toLowerCase().includes('bairro') ? '' : 'Bairro ';
      const nhFull = nhPrefix + neighborhoodName;
      
      locationLabel = `${nhFull}, ${this.formCity}`;
      if (this.selectedNeighborhood && this.selectedNeighborhood.lat && this.selectedNeighborhood.lng) {
        locationObj = { lat: this.selectedNeighborhood.lat, lng: this.selectedNeighborhood.lng };
      }
    }

    const fullQuery = `${this.formQuery}${neighborhoodName ? ' no ' + (neighborhoodName.toLowerCase().includes('bairro') ? neighborhoodName : 'Bairro ' + neighborhoodName) : ''} em ${this.formCity}, Brasil`;

    this.dispatchEvent(new CustomEvent('start-scan', {
      detail: { 
        query: fullQuery, 
        city: locationLabel, 
        mode: this.formMode,
        location: locationObj,
        cumulative: this.isCumulative
      }
    }));
  }

  private stopScan() {
    this.dispatchEvent(new CustomEvent('stop-scan'));
  }

  private openLeadDetails(lead: any) {
    this.dispatchEvent(new CustomEvent('lead-selected', { detail: lead }));
  }

  private startEnrichment() {
    this.dispatchEvent(new CustomEvent('start-enrichment', { bubbles: true, composed: true }));
  }

  createRenderRoot() {
    return this;
  }

  render() {
    const hasLeads = this.leads.length > 0;
    
    // Filtros inteligentes para botões de ação
    const leadsNeedingEnrichment = this.leads.filter(l => !l.formatted_phone_number || !l.photos || l.photos.length === 0);

    return html`
      <div id="leads" class="tabcontent showtab">
        <div class="manual-scan-form">
          <strong style="font-size: 14px; margin-bottom: 10px; display: block;">Fazer Varredura Manual</strong>
          
          <div style="display: flex; flex-direction: column; gap: 8px;">
            <input 
              type="text" 
              placeholder="O que buscar? (Ex: Marido de aluguel)" 
              .value=${this.formQuery}
              @input=${(e: any) => this.formQuery = e.target.value}
            />

            <div style="display: flex; gap: 4px;">
              <select @change=${this.onStateChange} style="width: 70px;">
                ${this.states.map(s => html`<option value="${s.sigla}" ?selected=${s.sigla === this.selectedStateSigla}>${s.sigla}</option>`)}
              </select>

              <select @change=${this.onCityChange} style="flex: 1;">
                <option value="">Cidade...</option>
                ${this.cities.map(c => html`<option value="${c.id}" ?selected=${c.id === this.selectedCityId}>${c.nome}</option>`)}
              </select>
            </div>

            <div style="display: flex; flex-direction: column; gap: 4px;">
              <div style="display: flex; gap: 4px;">
                <select 
                  style="flex: 1;"
                  .value=${this.selectedNeighborhood ? this.selectedNeighborhood.id : ''} 
                  @change=${this.onNeighborhoodChange}
                  ?disabled=${!!this.customNeighborhood}
                >
                  <option value="">Todos os Bairros (ou selecione um...)</option>
                  ${this.neighborhoods.map(n => html`<option value="${n.id}">${n.nome}</option>`)}
                </select>
              </div>
              
              <div style="display: flex; align-items: center; gap: 8px;">
                <div style="font-size: 11px; color: #666;">Ou digite o nome:</div>
                <input 
                  type="text" 
                  placeholder="Nome do bairro manual..." 
                  .value=${this.customNeighborhood}
                  @input=${(e: any) => {
                    this.customNeighborhood = e.target.value;
                    if (this.customNeighborhood) this.selectedNeighborhood = null;
                  }}
                  style="flex: 1; padding: 6px; font-size: 13px;"
                />
              </div>
            </div>

            <select
              .value=${this.formMode}
              @change=${(e: any) => this.formMode = e.target.value as any}
            >
              <option value="standard">Modo Padrão (Rápido)</option>
              <option value="exhaustive">Modo Exaustivo</option>
              <option value="micro">Modo Micro (Bairro a Bairro)</option>
            </select>

            <div style="display: flex; align-items: center; gap: 8px; padding: 5px 0;">
              <input 
                type="checkbox" 
                id="cumulative-toggle"
                .checked=${this.isCumulative}
                @change=${(e: any) => this.isCumulative = e.target.checked}
                style="width: 16px; height: 16px; cursor: pointer;"
              />
              <label for="cumulative-toggle" style="font-size: 13px; color: var(--color-text2); cursor: pointer;">
                Acumular resultados (não limpar lista)
              </label>
            </div>

            <button @click=${this.startManualScan} ?disabled=${this.isScanning || this.isEnriching}>
              Iniciar Varredura
            </button>
          </div>
          
          ${!this.isScanning && !this.isEnriching && hasLeads ? html`
            <button 
              style="margin-top: 10px; background: #f0f4ff; color: #1a73e8; border: 1px solid #d0dfff; padding: 12px; border-radius: 8px; font-weight: 600; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px; width: 100%; transition: all 0.2s;"
              @click=${this.startEnrichment}
              ?disabled=${leadsNeedingEnrichment.length === 0}
              style="opacity: ${leadsNeedingEnrichment.length === 0 ? '0.5' : '1'}">
              <span>✨</span> ${leadsNeedingEnrichment.length === 0 ? 'Tudo Enriquecido' : `Enriquecer Dados (${leadsNeedingEnrichment.length})`}
            </button>
          ` : ''}
        </div>

        ${this.isEnriching ? html`
          <div class="scan-progress-panel" style="background: #f8fafc; border: 1px solid #e2e8f0; padding: 16px; border-radius: 8px; margin: 0 16px 16px 16px;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
              <strong style="font-size: 13px; color: #1e293b;">✨ Enriquecendo Base...</strong>
              <span style="font-size: 12px; font-weight: 600; color: #1a73e8;">${this.enrichedCount}/${this.totalCount}</span>
            </div>
            <div style="height: 8px; background: #edf2f7; border-radius: 4px; overflow: hidden; margin-bottom: 8px;">
              <div style="height: 100%; background: #1a73e8; width: ${(this.enrichedCount / this.totalCount) * 100}%; transition: width 0.3s ease;"></div>
            </div>
            <p style="font-size: 12px; color: #64748b; margin: 0; text-align: center;">Buscando Telefones e Sites um por um...</p>
          </div>
        ` : ''}

        ${this.isWhatsAppEnriching ? html`
          <div class="scan-progress-panel" style="background: #f0fdf4; border: 1px solid #bbf7d0; padding: 16px; border-radius: 8px; margin: 0 16px 16px 16px;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
              <strong style="font-size: 13px; color: #166534;">💬 Sincronizando WhatsApp...</strong>
              <span style="font-size: 12px; font-weight: 600; color: #25D366;">${this.whatsappEnrichedCount}/${this.totalCount}</span>
            </div>
            <div style="height: 8px; background: #dcfce7; border-radius: 4px; overflow: hidden; margin-bottom: 8px;">
              <div style="height: 100%; background: #25D366; width: ${(this.whatsappEnrichedCount / this.totalCount) * 100}%; transition: width 0.3s ease;"></div>
            </div>
            <p style="font-size: 12px; color: #15803d; margin: 0; text-align: center;">Buscando fotos e bios de perfil...</p>
          </div>
        ` : ''}

        ${(this.isScanning || this.scanStatus) && !this.isEnriching ? html`
          <div class="scan-progress-panel">
            <strong style="font-size: 14px;">Status da Varredura</strong>
            <p style="font-size: 13px; color: #666; margin: 0;">${this.scanStatus}</p>
            ${this.isScanning ? html`
              <div class="progress-bar-bg">
                <div class="progress-bar-fill" style="width: ${this.scanProgress}%"></div>
              </div>
              <button 
                style="margin-top: 8px; background: #ffebee; color: #d32f2f; border: 1px solid #ffcdd2; border-radius: 4px; padding: 6px; cursor: pointer; font-size: 13px; font-weight: bold;"
                @click=${this.stopScan}>
                Parar Varredura
              </button>
            ` : ''}
          </div>
        ` : ''}

        <div class="leads-list">
          ${this.leads.length === 0 ? this.renderEmpty() : this.leads.map(lead => this.renderLeadCard(lead))}
        </div>
      </div>
    `;
  }

  private renderEmpty() {
    return html`
      <div style="text-align: center; padding: 40px 20px; color: #888;">
        <span style="font-size: 32px; display: block; margin-bottom: 12px;">📊</span>
        <p style="font-size: 14px; margin: 0;">Nenhum lead salvo ainda.</p>
      </div>
    `;
  }

  private renderLeadCard(lead: any) {
    return html`
      <div class="lead-card" @click=${() => this.openLeadDetails(lead)}>
        <h3>${lead.name}</h3>
        <p style="font-size: 12px; color: #666; margin: 0; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;">
          📍 ${lead.formatted_address || lead.vicinity || 'Endereço não disponível'}
        </p>
        ${lead.rating ? html`
          <div style="margin-top: 8px; font-size: 12px; font-weight: 500; color: #f5b400;">
            ⭐ ${lead.rating} (${lead.user_ratings_total || 0})
          </div>
        ` : ''}
      </div>
    `;
  }
}
